import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete,Button,Card,Checkbox,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,IconButton,Stack,TextField,Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useMutation,useQuery } from '@tanstack/react-query';
import { useContext,useEffect,useState } from 'react';
import { ColorResult,SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { FaBold,FaItalic } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context/UserContext';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import Clients,{ ClientType } from '../../../service/Clients';
import PrintService from '../../../service/PrintTemplate';
import queryClient from '../../../service/query';
import { printTemplate,PrintTemplateType } from '../../../types/print-template/types';
import { PreviewColor } from '../FormStyle';
import PrintPreviewCreateModal from './PrintPreviewCreateModal';

const customerService = new Clients();
const printService = new PrintService();

export default function PrintTemplateModal({ open, setOpen, selectedPrint, setSelectedPrint }
  : { open: boolean, setOpen: Function, selectedPrint: PrintTemplateType, setSelectedPrint: Function }) {

  const { hierarchyLabel } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const [showCustomer, setShowCustomer] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [showColor, setShowColor] = useState(selectedPrint && (selectedPrint.logo || selectedPrint.color) ? true : false);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (selectedPrint.headerText?.bold || selectedPrint.headerText?.italic || selectedPrint.headerText?.size || selectedPrint.headerText?.text) {
      setShowHeader(true);
    } if (selectedPrint.footerText?.bold || selectedPrint.footerText?.italic || selectedPrint.footerText?.size || selectedPrint.footerText?.text) {
      setShowFooter(true);
    }
    if (selectedPrint.cliente) {
      setShowCustomer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_CLIENTES'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const body = { ...token, empresa: token.userEmpresa };
        const result = await customerService.getAllClients(body, true);
        return result.clientList;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: 3
  });


  const { mutate: create, isPending: loadingCreate } = useMutation({
    mutationKey: ['CREATE_TEMPLATE'],
    mutationFn: async () => {
      const token = await AttToken();
      if (token) {
        const res = await printService.createPrintTemplate(token, { ...selectedPrint as printTemplate, logo: selectedPrint?.logo || null });
        return res;
      }
    },
    onSuccess: () => {
      setSelectedPrint(null);
      queryClient.resetQueries({ queryKey: ['GET_PRINT_TEMPLATE'] });
      toast.success(`${t('toast.templateCreated')}`);
      setOpen();
    }
  });

  const { mutate: edit, isPending: loadingEdit } = useMutation({
    mutationKey: ['EDIT_TEMPLATE'],
    mutationFn: async () => {
      toast.warn(`${t('toast.savingEdit')}`);
      const token = await AttToken();
      if (token) {
        await printService.editPrintTemplate(token, {
          ...selectedPrint,
          logo: selectedPrint?.logo || undefined, empresa: undefined
        }, selectedPrint.id as string);
      }
    },
    onSuccess: () => {
      setSelectedPrint(null);
      queryClient.resetQueries({ queryKey: ['GET_PRINT_TEMPLATE'] });
      toast.success(`${t('toast.templateEdited')}`);
      setOpen();
    }
  });

  const loading = isLoading || isFetching || loadingEdit || loadingCreate;

  function getBase64(file: File, cb: (s: string) => void) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      cb(reader.result as string);
    };
    reader.onerror = function (error) {
      console.error('Error: ', error);
    };
  }

  const renderImages = (File: FileList | null) => {
    if (File) {
      Array.from(File).map(e => getBase64(e, (i) => setSelectedPrint((prev: PrintTemplateType) => prev ? { ...prev, logo: i } : { logo: i })));
    }
  };

  const submit = () => {
    trackEventMatomo('PrintTemplateModal', 'click', 'button', 'salva');
    selectedPrint.id
      ? edit()
      : create();
  };

  const fontSizes = [
    { value: 12, label: '12px (Pequeno)' },
    { value: 14, label: '14px (Normal)' },
    { value: 16, label: '16px (Médio)' },
    { value: 18, label: '18px (Grande)' },
    { value: 20, label: '20px (Extra Grande)' },
    { value: 24, label: '24px (Título)' },
    { value: 32, label: '32px (Destaque)' }
  ];

  return (
    <>
      {openModal &&
        <PrintPreviewCreateModal
          printTemplate={selectedPrint}
          openModal={openModal}
          setOpenModal={setOpenModal}
        />}
      <Dialog
        fullWidth
        maxWidth='md'
        scroll='paper'
        open={open}
        onClose={() => { setOpen(false); setSelectedPrint(null); }}
      >
        <DialogTitle>{t('checklists.printConfig.configPrintTemplate')}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => { setOpen(false); setSelectedPrint(null); }}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          <Stack width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={2} direction='column'>
            <TextField
              label={t('checklists.printConfig.templateName')}
              fullWidth
              value={selectedPrint?.name}
              onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor')}
              onChange={(e) => {
                setSelectedPrint((prev: PrintTemplateType) => prev ? { ...prev, name: e.target.value } : { name: e.target.value });
              }}
            />
            <FormControlLabel control={<Checkbox checked={selectedPrint?.dafaultTemplate}
              onChange={(e) => {
                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                setSelectedPrint((prev: PrintTemplateType) => prev ? { ...prev, dafaultTemplate: e.target.checked } : { dafaultTemplate: e.target.checked });;
              }} />}
              label={t('checklists.printConfig.setDefault')} />
            <FormControlLabel control={<Checkbox checked={showCustomer}
              onChange={(e) => {
                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                setShowCustomer(e.target.checked);
              }} />}
              label={t('checklists.printConfig.customerLimit')} />
            {showCustomer && (
              <Autocomplete
                loading={loading}
                disabled={loading}
                fullWidth
                getOptionLabel={option => option.label}
                getOptionKey={option => option.value}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={selectedPrint?.cliente ? {
                  value: selectedPrint?.cliente,
                  label: data?.find((ele: ClientType) => ele.id === selectedPrint?.cliente)?.nome
                } : null}
                options={
                  data?.sort((a: ClientType, b: ClientType) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
                    ?.filter((e: ClientType) => e.id !== '')
                    ?.map((ele: ClientType) => ({
                      label: ele.nome,
                      value: ele.id,
                    })) ?? []}
                onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'select', 'seleciona valor')}
                onChange={(_event, newValue: { label: string, value: string } | null) => {
                  setSelectedPrint((prev: PrintTemplateType) => prev ? { ...prev, cliente: newValue?.value } : { cliente: newValue?.value });
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    <span>{option.label}</span>
                  </li>)}
                renderInput={(params) => <TextField {...params} label={hierarchyLabel.cliente} />}
              />
            )}
            <FormControlLabel control={<Checkbox checked={showHeader}
              onChange={(e) => {
                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                setShowHeader(e.target.checked);
              }} />}
              label={t('checklists.printConfig.headerText')} />
            {showHeader
              && (<>
                <TextField
                  rows={3}
                  maxRows={3}
                  fullWidth
                  value={selectedPrint?.headerText?.text}
                  onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'textarea', 'edita valor')}
                  onChange={(e) =>
                    setSelectedPrint((prev: PrintTemplateType) => prev
                      ? { ...prev, headerText: { ...prev.headerText, text: e.target.value } }
                      : { headerText: { text: e.target.value } })
                  }
                  sx={{
                    fontSize: selectedPrint?.headerText?.size,
                    fontWeight: selectedPrint?.headerText?.bold ? 600 : 500,
                    fontStyle: selectedPrint?.headerText?.italic ? 'italic' : 'normal'
                  }}
                />
                <Card sx={{ backgroundColor: '#f5f5f5', padding: 1 }}>
                  <Stack direction='row' alignItems='center' justifyContent='flex-start' spacing={1}>
                    <FormControl sx={{ width: 200 }} size="small">
                      <InputLabel id="font-size-select-label">{t('checklists.printConfig.fontSize')}</InputLabel>
                      <Select
                        labelId="font-size-select-label"
                        id="font-size-select"
                        value={selectedPrint?.headerText?.size}
                        label={t('checklists.printConfig.fontSize')}
                        onChange={(e) => {
                          setSelectedPrint((prev: PrintTemplateType) => prev
                            ? { ...prev, headerText: { ...prev.headerText, size: e.target.value } }
                            : { headerText: { size: e.target.value } });
                        }}
                        sx={{
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center'
                          }
                        }}
                      >
                        {fontSizes.map((size) => (
                          <MenuItem
                            key={size.value}
                            value={size.value}
                            sx={{
                              fontSize: size.value,
                              padding: '8px 16px'
                            }}
                          >
                            {size.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      color={selectedPrint?.headerText?.bold ? 'info' : 'default'}
                      onClick={() => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'button', 'negrito');
                        setSelectedPrint((prev: PrintTemplateType) => prev
                          ? { ...prev, headerText: { ...prev.headerText, bold: !selectedPrint?.headerText?.bold } }
                          : { headerText: { bold: !selectedPrint?.headerText?.bold } });
                      }}
                    >
                      <FaBold />
                    </IconButton>
                    <IconButton
                      color={selectedPrint?.headerText?.italic ? 'info' : 'default'}
                      onClick={() => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'button', 'italico');
                        setSelectedPrint((prev: PrintTemplateType) => prev
                          ? { ...prev, headerText: { ...prev.headerText, italic: !selectedPrint?.headerText?.italic } }
                          : { headerText: { italic: !selectedPrint?.headerText?.italic } });
                      }}
                    >
                      <FaItalic />
                    </IconButton>
                  </Stack>
                </Card>
              </>
              )}
            <FormControlLabel control={<Checkbox checked={showFooter}
              onChange={(e) => {
                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                setShowFooter(e.target.checked);
              }} />}
              label={t('checklists.printConfig.footerText')} />
            {showFooter
              && (<>
                <TextField
                  rows={3}
                  maxRows={3}
                  fullWidth
                  value={selectedPrint?.footerText?.text}
                  onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'textarea', 'edita valor')}
                  onChange={(e) =>
                    setSelectedPrint((prev: PrintTemplateType) => prev
                      ? { ...prev, footerText: { ...prev.footerText, text: e.target.value } }
                      : { footerText: { text: e.target.value } })
                  }
                  sx={{
                    fontSize: selectedPrint?.footerText?.size,
                    fontWeight: selectedPrint?.footerText?.bold ? 600 : 500,
                    fontStyle: selectedPrint?.footerText?.italic ? 'italic' : 'normal'
                  }}
                />
                <Card sx={{ backgroundColor: '#f5f5f5', padding: 1 }}>
                  <Stack direction='row' alignItems='center' justifyContent='flex-start' spacing={1}>
                    <FormControl sx={{ width: 200 }} size="small">
                      <InputLabel id="font-size-select-label">{t('checklists.printConfig.fontSize')}</InputLabel>
                      <Select
                        labelId="font-size-select-label"
                        id="font-size-select"
                        value={selectedPrint?.footerText?.size}
                        label={t('checklists.printConfig.fontSize')}
                        onChange={(e) => {
                          setSelectedPrint((prev: PrintTemplateType) => prev
                            ? { ...prev, footerText: { ...prev.footerText, size: e.target.value } }
                            : { footerText: { size: e.target.value } });
                        }}
                        sx={{
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center'
                          }
                        }}
                      >
                        {fontSizes.map((size) => (
                          <MenuItem
                            key={size.value}
                            value={size.value}
                            sx={{
                              fontSize: size.value,
                              padding: '8px 16px'
                            }}
                          >
                            {size.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      color={selectedPrint?.footerText?.bold ? 'info' : 'default'}
                      onClick={() => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'button', 'negrito');
                        setSelectedPrint((prev: PrintTemplateType) => prev
                          ? { ...prev, footerText: { ...prev.footerText, bold: !selectedPrint?.footerText?.bold } }
                          : { footerText: { bold: !selectedPrint?.footerText?.bold } });
                      }}
                    >
                      <FaBold />
                    </IconButton>
                    <IconButton
                      color={selectedPrint?.footerText?.italic ? 'info' : 'default'}
                      onClick={() => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'button', 'italico');
                        setSelectedPrint((prev: PrintTemplateType) => prev
                          ? { ...prev, footerText: { ...prev.footerText, italic: !selectedPrint?.footerText?.italic } }
                          : { footerText: { italic: !selectedPrint?.footerText?.italic } });
                      }}
                    >
                      <FaItalic />
                    </IconButton>
                  </Stack>
                </Card>
              </>
              )}
            <FormControlLabel control={<Checkbox checked={showColor}
              onChange={(e) => {
                trackEventMatomo('PrintTemplateModal', 'click', 'input', 'habilita valor');
                setShowColor(e.target.checked);
              }} />}
              label={t('checklists.printConfig.graphicSettings')} />
            {showColor && (
              <Stack width='100%' alignItems='flex-start' direction='row' justifyContent='space-between'>
                <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' spacing={1}>
                  <Stack direction='row' justifyContent='flex-start' alignItems='center' spacing={1}>
                    <Typography color='textDisabled' fontWeight={600}>{t('checklists.printConfig.backgroundColor')}</Typography>
                    <PreviewColor style={{ backgroundColor: selectedPrint?.color }} />
                  </Stack>
                  <SketchPicker
                    color={selectedPrint?.color}
                    onChangeComplete={
                      (color: ColorResult) => {
                        trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita valor');
                        setSelectedPrint((prev: PrintTemplateType) => prev ? { ...prev, color: color.hex } : { color: color.hex });
                      }
                    }
                    disableAlpha
                    presetColors={['#fff', '#000', '#f00', '#0f0', '#00f']}
                  />
                </Stack>
                <Card>
                  <Stack direction='column' alignItems='center' justifyContent='center' spacing={1} padding={1}>
                    {!selectedPrint?.logo ? <>
                      <Typography fontWeight={600} color='textDisabled'>{t('checklists.printConfig.headerPicture')}</Typography>
                      <span className='button is-success'>
                        <input
                          accept="image/png, image/jpeg"
                          value={""}
                          className="file-input"
                          type="file"
                          onChange={(e) => {
                            trackEventMatomo('PrintTemplateModal', 'click', 'input', 'edita imagem');
                            renderImages(e.target.files);
                          }}
                        />
                        {t('users.userRegister.selectMessage')}
                        <AddPhotoAlternateIcon />
                      </span>
                    </> :
                      <Stack sx={{ position: 'relative' }} direction='row-reverse'>
                        <IconButton sx={{ position: 'absolute' }} color='error' onClick={() => setSelectedPrint((prev: PrintTemplateType) => prev ? { ...prev, logo: '' } : { logo: '' })}>
                          <DeleteIcon />
                        </IconButton>
                        <img alt="Logo da empresa" src={selectedPrint?.logo} style={{ maxWidth: 300, maxHeight: 300 }} />
                      </Stack>}
                  </Stack>
                </Card>
              </Stack>
            )}
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">{t('status')}</InputLabel>
              <Select
                label={t('status')}
                onClick={() => trackEventMatomo('PrintTemplateModal', 'click', 'select', 'seleciona valor')}
                onChange={(e) => {
                  setSelectedPrint({ ...selectedPrint, status: e.target.value === 'ativo' ? true : false });
                }}
                defaultValue={selectedPrint?.status ? 'ativo' : 'inativo'}
              >
                <MenuItem value={'ativo'}>{t('active')}</MenuItem>
                <MenuItem value={'inativo'}>{t('inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              trackEventMatomo('PrintTemplateModal', 'click', 'button', 'abre modal');
              setOpenModal(true);
            }}
          >
            {t('checklists.printConfig.preview')}
          </Button>
          <Button
            type='submit'
            onClick={() => submit()}
            disabled={loading}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
}
